import React, { useContext, useState } from 'react'
import {
  useOrderAndFpMemberCouponsQuery,
  useInitiateCheckoutMutation,
  useOrderAndHhMemberCouponsQuery,
} from 'graphQL/dinein/generate/operations'
import ModalWarningTotalZeroDue from 'components/modals/ModalWarningTotalZeroDue'
import { useSelector } from 'react-redux'
import { buffetSelector } from 'store/slices/buffetSlice'
import { dineInClient } from 'variables/services'
import useValidateCurrentOrder, { OrderInvalidEnum } from 'hooks/buffet/useValidateCurrentOrder'
import useHandleBuffetOrder from 'hooks/buffet/useHandleBuffetOrder'
import { ModalEnum } from 'components/buffet/BuffetModals'
import { ScreenEnum } from 'components/buffet/BuffetOverlayPage'
import { AppContext } from 'contexts/AppProvider'
import { THEMES_UI } from 'brands'
import usePromotion from 'hooks/dine-in/usePromotion'
import useCombineCoupon from 'hooks/buffet/useCombineCoupon'
import useLoadingPage from 'hooks/useLoadingPage'
import { useTranslation } from 'react-i18next'
import { MAXIMUM_SHARE_COUPONS } from 'variables/dineIn'
import ModalLimitSharingCoupons from 'components/dine-in/modal/ModalLimitSharingCoupons'
import ModalConfirmCombineCoupon from 'components/dine-in/modal/ModalConfirmCombineCoupon'
import LoadingBrand from 'components/LoadingBrand'
import PromotionList from 'components/PromotionList'
import { useNavigate } from 'react-router-dom'
import { SubPageName } from 'enums/buffet'

const PromotionController = () => {
  const buffet = useSelector(buffetSelector)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { themeUI } = useContext(AppContext)
  const { isLoadingPage, setIsLoadingPage } = useLoadingPage({ initialIsLoadingPage: true })
  const [isCashVoucherErrorZerodue, setIsCashVoucherErrorZerodue] = useState(false)
  const { handleValidateResult } = useHandleBuffetOrder({
    skipModal: [ModalEnum.TimeUp, ModalEnum.ReadyToCheckout, ModalEnum.ContinueCheckout],
    skipScreen: [ScreenEnum.CreditCardFailed, ScreenEnum.ResubmitPayment],
  })
  const { validateCurrentOrder } = useValidateCurrentOrder()
  const [initiateCheckout] = useInitiateCheckoutMutation({
    client: dineInClient,
  })
  const { data: orderAndFpMemberCouponsResult, refetch: refetchOrderAndFpMemberCoupons } =
    useOrderAndFpMemberCouponsQuery({
      client: dineInClient,
      fetchPolicy: 'no-cache',
      skip: themeUI !== THEMES_UI.GON,
      variables: { orderID: buffet.table.orderID },
      onCompleted: async (result) => {
        setIsLoadingPage(false)
        const validateResult = await customValidateOrder()
        if (
          validateResult.invalid === OrderInvalidEnum.ReadyToCheckout &&
          result.orderAndFPMemberCoupons.order.userID === 0
        ) {
          await initiateCheckout({
            variables: {
              orderID: buffet.table.orderID,
              override: true,
            },
          })
          await refetchOrderAndFpMemberCoupons()
        }
      },
    })

  const { data: orderAndHhMemberCouponsResult, refetch: refetchOrderAndHhMemberCoupons } =
    useOrderAndHhMemberCouponsQuery({
      client: dineInClient,
      fetchPolicy: 'no-cache',
      skip: themeUI === THEMES_UI.GON,
      variables: { orderID: buffet.table.orderID },
      onCompleted: async (result) => {
        setIsLoadingPage(false)
        const validateResult = await customValidateOrder()
        if (
          validateResult.invalid === OrderInvalidEnum.ReadyToCheckout &&
          result.orderAndHHMemberCoupons.order.userID === 0
        ) {
          await initiateCheckout({
            variables: {
              orderID: buffet.table.orderID,
              override: true,
            },
          })
          await refetchOrderAndFpMemberCoupons()
        }
      },
    })

  const { orderAndMemberCoupons, memberCouponsFP, memberPromotions, generalPromotions, memberCouponsHH } = usePromotion(
    { orderAndFpMemberCouponsResult, orderAndHhMemberCouponsResult }
  )
  const { handleClickCombineCoupon, confirmCombineCoupon, modalLimitSharingCoupons, modalCombineCoupons } =
    useCombineCoupon()

  const customValidateOrder = () => {
    const validateResult = validateCurrentOrder(buffet.currentOrder)
    if (
      validateResult.isOK ||
      validateResult.invalid === OrderInvalidEnum.OwnerNeedToCheckout ||
      validateResult.invalid === OrderInvalidEnum.CreditCardFailed ||
      validateResult.invalid === OrderInvalidEnum.PaymentIsNotCompleted
    ) {
      return { ...validateResult, isOK: true }
    } else {
      handleValidateResult(validateResult, buffet.currentOrder)
      return validateResult
    }
  }

  const handleClickRefreshCoupon = async () => {
    setIsLoadingPage(true)
    await onRefetch()
    setIsLoadingPage(false)
  }

  const onRefetch = async () =>
    themeUI === THEMES_UI.GON ? await refetchOrderAndFpMemberCoupons() : await refetchOrderAndHhMemberCoupons()

  return (
    <div>
      <PromotionList
        orderID={buffet.table.orderID}
        isLoadingPage={isLoadingPage}
        orderAndMemberCoupons={orderAndMemberCoupons}
        memberCouponsFP={memberCouponsFP}
        memberCouponsHH={memberCouponsHH}
        memberPromotions={memberPromotions}
        generalPromotions={generalPromotions}
        onRefetch={onRefetch}
        onClickCombineCoupon={handleClickCombineCoupon}
        onClickRefreshCoupon={handleClickRefreshCoupon}
        onClickBack={() => navigate(SubPageName.Checkout)}
        allowedSharingCoupon={buffet.allowedSharingCoupon}
        setIsCashVoucherErrorZerodue={setIsCashVoucherErrorZerodue}
      />

      <ModalWarningTotalZeroDue
        isOpen={isCashVoucherErrorZerodue}
        onClose={() => setIsCashVoucherErrorZerodue(false)}
        onClickButton={() => setIsCashVoucherErrorZerodue(false)}
      />

      <ModalLimitSharingCoupons
        total={MAXIMUM_SHARE_COUPONS}
        isOpen={modalLimitSharingCoupons.open}
        onRequestClose={() => modalLimitSharingCoupons.setOpen(false)}
        btnAccept={{
          content: t('dineIn.modal.limitSharingCoupons.accept'),
          onClick: () => modalLimitSharingCoupons.setOpen(false),
        }}
      />

      <ModalConfirmCombineCoupon
        isOpen={modalCombineCoupons.open}
        onRequestClose={() => {
          modalCombineCoupons.setOpen(false)
        }}
        btnAccept={{
          content: t('dineIn.modal.shareCoupon.confirm'),
          isProcessing: modalCombineCoupons.processing,
          onClick: confirmCombineCoupon,
        }}
        btnCancel={{
          content: t('dineIn.modal.shareCoupon.cancel'),
          outline: true,
          onClick: () => modalCombineCoupons.setOpen(false),
        }}
      />
      <LoadingBrand show={isLoadingPage} />
    </div>
  )
}

export default PromotionController
